/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialLibrary/MaterialLibrary.tsx.
 */

import React, { useEffect, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import publicAxiosClient from "../../../../api/publicAxiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import MaterialCard from "../../../../components/common/MaterialCard";
import {
  buildCatalogImageById,
  resolveMaterialImageFromCatalog,
} from "../../../../utils/resolveUploadAssetUrl";
import AnalysisBanner from "../../../../components/common/AnalysisBanner";
import "./MaterialLibrary.css";

interface MaterialLibraryItem {
  id: number;
  name: string;
  code: string;
  description?: string | null;
  image?: string | null;
  imageUrl?: string | null;
  image_url?: string | null;
  sampleSize?: { value: number; unit?: string } | null;
  average?: { value: number; unit?: string } | null;
  min?: { value: number; unit?: string } | null;
  max?: { value: number; unit?: string } | null;
  standardDeviation?: { value: number; unit?: string } | null;
}

const MaterialLibrary: React.FC = () => {
  const [materials, setMaterials] = useState<MaterialLibraryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchMaterials = async () => {
      try {
        setLoading(true);
        const [libraryRes, catalogRes] = await Promise.all([
          axiosClient.get(API_URLS.USER_DASHBOARD.MATERIAL_LIBRARY),
          axiosClient.get(API_URLS.MATERIALS.LIST).catch(() =>
            publicAxiosClient.get(API_URLS.MATERIALS.LIST).catch(() => ({
              data: { data: [] },
            }))
          ),
        ]);

        if (!libraryRes.data?.success) {
          setMaterials([]);
          setError(libraryRes.data?.message || "Failed to load materials.");
          return;
        }

        const materialsData = Array.isArray(libraryRes.data?.data)
          ? libraryRes.data.data
          : [];

        const catalogItems = Array.isArray(catalogRes.data?.data)
          ? catalogRes.data.data
          : Array.isArray(catalogRes.data)
            ? catalogRes.data
            : [];

        const catalogImageById = buildCatalogImageById(catalogItems);

        setMaterials(
          materialsData.map((material: MaterialLibraryItem) => ({
            ...material,
            imageUrl:
              resolveMaterialImageFromCatalog(material, catalogImageById) || null,
          }))
        );
        setError("");
      } catch (err: any) {
        console.error("Error fetching materials:", err);
        setError(
          err.response?.data?.message ||
            "Failed to load materials. Please try again later."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchMaterials();
  }, []);

  if (loading) {
    return (
      <div className="material-library-container">
        <AnalysisBanner title="Material Library" />
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Loading materials...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="material-library-container">
        <AnalysisBanner title="Material Library" />
        <div className="error-state">
          <div className="error-icon">⚠️</div>
          <h3>Error Loading Materials</h3>
          <p>{error}</p>
          <button className="retry-btn" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="material-library-container">
      <AnalysisBanner title="Material Library" />

      {materials.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📦</div>
          <h3>No Materials Available</h3>
          <p>Materials will appear here once they are added to the system.</p>
        </div>
      ) : (
        <div className="material-list">
          {materials.map((material) => (
            <MaterialCard
              key={material.id}
              id={material.id}
              name={material.name}
              code={material.code}
              imageUrl={material.imageUrl}
              apiBaseUrl={axiosClient.defaults.baseURL}
              stats={{
                sampleSize: material.sampleSize?.value ?? 0,
                average: material.average?.value ?? 0,
                min: material.min?.value ?? 0,
                max: material.max?.value ?? 0,
                standardDeviation: material.standardDeviation?.value ?? 0,
              }}
              showAllDatasetsStat={false}
              showExploreButton={false}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/** React component — Material Library. */
export default MaterialLibrary;

